import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ErrorMessage } from '@/components/ui/errormessage'
import { InlineHelpTooltip } from '@/components/ui/inline-help-tooltip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn, getErrorMessage } from '@/lib/utils'
import { useAuthStore, useUser } from '@/stores/authStore'
import { isConsoleDeployment } from '@/utils/deployment'
import {
	type Announcements,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	MouseSensor,
	TouchSensor,
	type UniqueIdentifier,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, RotateCcw } from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { BAR_SLOTS, NOT_IN_MENU, arrangeFooterItems, describePlace, rearrange } from './footerBarModel'
import { saveFooterBarPrefs, useFooterBarPrefs } from './footerBarPrefs'
import type { FooterItem } from './useFooterItems'

const NAME_SAVE_DELAY_MS = 800

interface CustomizeDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	items: FooterItem[]
}

export function CustomizeDialog({ open, onOpenChange, items }: CustomizeDialogProps) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='flex max-h-[85vh] flex-col gap-0 p-0' data-testid='sidebar-customize-dialog'>
				<DialogHeader className='px-4 pt-4 pb-3'>
					<DialogTitle>Customize</DialogTitle>
				</DialogHeader>
				{!isConsoleDeployment() && <NameField />}
				<BarEditor items={items} />
			</DialogContent>
		</Dialog>
	)
}

/** Saves after a pause in typing or on blur. Mounted only while the dialog is open, so it starts from the saved name. */
function NameField() {
	const savedName = useUser()?.name ?? ''
	const [name, setName] = useState(savedName)
	const [error, setError] = useState('')
	const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
	const pending = useRef<{ value: string; timer: ReturnType<typeof setTimeout> } | null>(null)

	const save = async (value: string) => {
		if (pending.current) {
			clearTimeout(pending.current.timer)
			pending.current = null
		}
		const trimmed = value.trim()
		if (!trimmed) {
			setError('Name is required')
			return
		}
		if (trimmed === useAuthStore.getState().user?.name) {
			return
		}
		setStatus('saving')
		try {
			await useAuthStore.getState().updateUserName(trimmed)
			setStatus('saved')
		} catch (saveError) {
			setStatus('idle')
			toast.error(getErrorMessage(saveError))
		}
	}

	// Closing the dialog mid pause still saves what was typed.
	const flushOnUnmount = useRef(save)
	flushOnUnmount.current = save
	useEffect(
		() => () => {
			if (pending.current) {
				void flushOnUnmount.current(pending.current.value)
			}
		},
		[],
	)

	return (
		<div className='flex flex-col gap-1.5 border-b px-4 pb-4'>
			<div className='flex items-center justify-between'>
				<Label htmlFor='sidebar-customize-name'>Name</Label>
				{status === 'saving' && <Spinner className='size-3' data-testid='sidebar-customize-name-saving' />}
				{status === 'saved' && (
					<span className='text-control text-muted-foreground' data-testid='sidebar-customize-name-saved'>
						Saved
					</span>
				)}
			</div>
			<Input
				id='sidebar-customize-name'
				value={name}
				onChange={e => {
					const value = e.target.value
					setName(value)
					setError('')
					setStatus('idle')
					if (pending.current) {
						clearTimeout(pending.current.timer)
					}
					pending.current = { value, timer: setTimeout(() => void save(value), NAME_SAVE_DELAY_MS) }
				}}
				onBlur={() => void save(name)}
				className={cn(error && 'border-destructive')}
				data-testid='sidebar-customize-name'
			/>
			<ErrorMessage message={error} data-testid='sidebar-customize-name-error' />
		</div>
	)
}

function BarEditor({ items }: { items: FooterItem[] }) {
	const prefs = useFooterBarPrefs()
	const { shown, hidden } = arrangeFooterItems(items, prefs)
	const listIds = [...shown.map(item => item.id), NOT_IN_MENU, ...hidden.map(item => item.id)]

	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
		// A long press, so a finger on a row still scrolls the list.
		useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	const reorder = (activeId: UniqueIdentifier, overId: UniqueIdentifier) =>
		arrayMove(listIds, listIds.indexOf(String(activeId)), listIds.indexOf(String(overId)))
	const titleOf = (id: UniqueIdentifier) => items.find(item => item.id === id)?.title
	const announcements: Announcements = {
		onDragStart: ({ active }) => `Picked up ${titleOf(active.id)}, ${describePlace(listIds, String(active.id))}.`,
		// Over itself on pick up, which would talk over the pick up message.
		onDragOver: ({ active, over }) =>
			over && over.id !== active.id
				? `${titleOf(active.id)}, ${describePlace(reorder(active.id, over.id), String(active.id))}.`
				: undefined,
		onDragEnd: ({ active, over }) =>
			`Dropped ${titleOf(active.id)}, ${describePlace(over ? reorder(active.id, over.id) : listIds, String(active.id))}.`,
		onDragCancel: ({ active }) => `Cancelled. ${titleOf(active.id)} is ${describePlace(listIds, String(active.id))}.`,
	}

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) {
			return
		}
		const ids = reorder(active.id, over.id)
		const split = ids.indexOf(NOT_IN_MENU)
		saveFooterBarPrefs(split === 0 ? undefined : rearrange(prefs, ids.slice(0, split), ids.slice(split + 1)))
	}

	return (
		<>
			<div className='flex items-center justify-between gap-3 px-4 pt-3'>
				<p className='flex items-center gap-2 text-body font-medium' data-testid='sidebar-customize-menu-heading'>
					Menu
					<span className='text-badge font-normal text-muted-foreground'>Top {BAR_SLOTS} in bar</span>
					<InlineHelpTooltip
						label='About the menu'
						content='Drag items here to add them to the menu'
						dataTestId='sidebar-customize-menu-info'
					/>
				</p>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							type='button'
							variant='outline'
							size='icon'
							aria-label='Reset to default'
							onClick={() => saveFooterBarPrefs(undefined)}
							className='size-control text-muted-foreground'
							data-testid='sidebar-customize-reset'>
							<RotateCcw aria-hidden />
						</Button>
					</TooltipTrigger>
					<TooltipContent side='top'>Reset to default</TooltipContent>
				</Tooltip>
			</div>
			<div className='min-h-0 flex-1 overflow-y-auto px-2 pt-1 pb-3'>
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					accessibility={{ announcements }}
					onDragEnd={handleDragEnd}>
					<SortableContext items={listIds} strategy={verticalListSortingStrategy}>
						{shown.map((item, index) => (
							<Fragment key={item.id}>
								<SortableRow item={item} />
								{index === BAR_SLOTS - 1 && shown.length > BAR_SLOTS && (
									<Separator className='my-1' data-testid='sidebar-customize-bar-divider' />
								)}
							</Fragment>
						))}
						<NotInMenuHeading />
						{hidden.map(item => (
							<SortableRow key={item.id} item={item} />
						))}
						{hidden.length === 0 && (
							<EmptyHint testId='sidebar-customize-not-in-menu-empty'>
								Drag items here to take them out of the menu
							</EmptyHint>
						)}
					</SortableContext>
				</DndContext>
			</div>
		</>
	)
}

/** Sortable so items can be dropped across it, never dragged itself. */
function NotInMenuHeading() {
	const { setNodeRef, transform, transition } = useSortable({ id: NOT_IN_MENU, disabled: { draggable: true } })
	return (
		<p
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			className='px-2 pt-3 pb-1 text-body font-medium'
			data-testid='sidebar-customize-not-in-menu-heading'>
			Not in menu
		</p>
	)
}

function EmptyHint({ testId, children }: { testId: string; children: string }) {
	return (
		<p
			className='my-0.5 flex h-9 items-center rounded-md border border-dashed px-2 text-control text-muted-foreground'
			data-testid={testId}>
			{children}
		</p>
	)
}

function SortableRow({ item }: { item: FooterItem }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id })
	return (
		<div
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			className={cn(
				'flex h-9 cursor-grab items-center gap-2 rounded-md pr-1 text-body outline-none select-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50',
				isDragging && 'relative z-10 cursor-grabbing opacity-80',
			)}
			data-testid={`sidebar-customize-row-${item.id}`}
			{...attributes}
			{...listeners}>
			<span className='flex size-control shrink-0 items-center justify-center text-muted-foreground'>
				<GripVertical className='size-(--icon-standalone)' aria-hidden />
			</span>
			<item.icon className='size-(--icon-standalone) text-muted-foreground' aria-hidden />
			<span className='min-w-0 flex-1 truncate'>{item.title}</span>
		</div>
	)
}
